/**
 * ErrorBoundary Component
 *
 * React error boundary that catches JavaScript errors in child components.
 * Displays a friendly error message with retry and navigation options.
 */

import { Component, type ReactNode, type ErrorInfo, type CSSProperties } from 'react';

/**
 * Props for ErrorBoundary component
 */
export interface ErrorBoundaryProps {
  /** Child components to wrap */
  children: ReactNode;
  /** Optional fallback component to render on error */
  fallback?: ReactNode;
  /** Optional callback when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

/**
 * State for ErrorBoundary component
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary catches JavaScript errors in child components
 * and displays a fallback UI instead of crashing the app.
 *
 * @example
 * ```tsx
 * <ErrorBoundary onError={(error) => logError(error)}>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state when an error is caught
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Log error information when an error is caught
   */
  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Store error info in state
    this.setState({ errorInfo });

    // Log to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call optional error callback
    this.props.onError?.(error, errorInfo);
  }

  /**
   * Reset the error state and retry rendering children
   */
  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Navigate to climb page
   */
  handleGoHome = (): void => {
    window.location.href = '/climb';
  };

  /**
   * Reload the page
   */
  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      // Render custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Render default error UI
      return (
        <div style={containerStyle} role="alert" aria-live="assertive">
          <div style={contentStyle}>
            {/* Error Icon */}
            <div style={iconContainerStyle}>
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M12 9V13M12 17H12.01"
                  stroke="#ef4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.29 3.86001L1.82002 18C1.64539 18.3024 1.55299 18.6453 1.55201 18.9945C1.55103 19.3437 1.6415 19.6871 1.81445 19.9905C1.98741 20.2939 2.2371 20.5467 2.53849 20.7239C2.83988 20.901 3.18237 20.9962 3.53002 21H20.47C20.8177 20.9962 21.1602 20.901 21.4616 20.7239C21.7629 20.5467 22.0126 20.2939 22.1856 19.9905C22.3585 19.6871 22.449 19.3437 22.448 18.9945C22.447 18.6453 22.3546 18.3024 22.18 18L13.71 3.86001C13.5318 3.56611 13.2807 3.32313 12.9812 3.15449C12.6817 2.98585 12.3438 2.89726 12 2.89726C11.6563 2.89726 11.3183 2.98585 11.0188 3.15449C10.7194 3.32313 10.4683 3.56611 10.29 3.86001Z"
                  stroke="#ef4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Title */}
            <h1 style={titleStyle}>Something went wrong</h1>

            {/* Description */}
            <p style={descriptionStyle}>
              We&apos;re sorry, but something unexpected happened. Our team has been notified and is
              working on a fix.
            </p>

            {/* Error Details (Development only) */}
            {process.env['NODE_ENV'] === 'development' && this.state.error && (
              <details style={detailsStyle}>
                <summary style={summaryStyle}>Error Details</summary>
                <pre style={errorTextStyle}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            {/* Actions */}
            <div style={actionsStyle}>
              <button type="button" onClick={this.handleRetry} style={primaryButtonStyle}>
                Try Again
              </button>
              <button type="button" onClick={this.handleReload} style={secondaryButtonStyle}>
                Reload Page
              </button>
              <button type="button" onClick={this.handleGoHome} style={tertiaryButtonStyle}>
                Go to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#fef2f2',
  padding: '20px',
};

const contentStyle: CSSProperties = {
  maxWidth: '500px',
  textAlign: 'center',
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  padding: '40px',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
};

const iconContainerStyle: CSSProperties = {
  marginBottom: '24px',
  display: 'flex',
  justifyContent: 'center',
};

const titleStyle: CSSProperties = {
  margin: '0 0 12px 0',
  fontSize: '1.5rem',
  fontWeight: 600,
  color: '#1f2937',
};

const descriptionStyle: CSSProperties = {
  margin: '0 0 24px 0',
  fontSize: '1rem',
  color: '#6b7280',
  lineHeight: 1.6,
};

const detailsStyle: CSSProperties = {
  marginBottom: '24px',
  textAlign: 'left',
};

const summaryStyle: CSSProperties = {
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#6b7280',
  marginBottom: '8px',
};

const errorTextStyle: CSSProperties = {
  margin: 0,
  padding: '12px',
  fontSize: '0.75rem',
  fontFamily: 'monospace',
  color: '#ef4444',
  backgroundColor: '#fef2f2',
  borderRadius: '6px',
  overflow: 'auto',
  maxHeight: '200px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const primaryButtonStyle: CSSProperties = {
  padding: '12px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  color: '#ffffff',
  backgroundColor: '#3b82f6',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

const secondaryButtonStyle: CSSProperties = {
  padding: '12px 24px',
  fontSize: '1rem',
  fontWeight: 500,
  color: '#374151',
  backgroundColor: '#ffffff',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

const tertiaryButtonStyle: CSSProperties = {
  padding: '12px 24px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#6b7280',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  textDecoration: 'underline',
};

export default ErrorBoundary;
